import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Loader2,
  MessageCircle,
  MessageSquareHeart,
  RotateCcw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getApiUrl } from '@/config/mercadopago'
import { trackCustomEvent } from '@/config/analytics'
import { getCookie, hashPII, pushEvent } from '@/config/gtm'
import {
  CHECKLIST_CATEGORIAS,
  CHECKLIST_PERGUNTAS,
  CHECKLIST_RESPOSTAS,
  calcularResultadoChecklist,
} from '@/data/checklistProntidao'

const ATTRIBUTION_STORAGE_KEY = 'consultoria-pre-cirurgica-attribution'
const DEFAULT_PAGE_LABEL = 'consultoria_pre_cirurgica'
const WHATSAPP_NUMBER = '5585982286045'

const LEAD_DATA_INICIAL = {
  nome: '',
  telefone: '',
  email: '',
  aceita_privacidade: false,
  quer_avaliacao: true,
  quer_contratar: false,
}

function formatarTelefone(valor) {
  const numeros = valor.replace(/\D/g, '').slice(0, 11)

  if (numeros.length <= 2) return numeros
  if (numeros.length <= 7) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`
  if (numeros.length <= 10) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`
  }

  return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`
}

function obterDigitosTelefone(valor) {
  return valor.replace(/\D/g, '')
}

function capturarAttributionDaUrl() {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const novosDados = {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    gclid: params.get('gclid') || '',
    fbclid: params.get('fbclid') || '',
  }

  const possuiParametro = Object.values(novosDados).some(Boolean)
  if (possuiParametro) {
    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(novosDados))
  }
}

function obterAttributionSalva() {
  if (typeof window === 'undefined') return {}

  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function obterTomCategoria(nivel) {
  if (nivel === 'Bem encaminhado') return 'border-teal/30 bg-teal/5'
  if (nivel === 'Precisa de ajustes') return 'border-amber-300/60 bg-amber-50'
  return 'border-red-300/60 bg-red-50'
}

function montarMensagemWhatsApp(tipo, lead, resultado) {
  const prefixo =
    tipo === 'avaliacao'
      ? 'Olá! Fiz o Checklist de Prontidão para Cirurgia e quero uma avaliação gratuita do concierge.'
      : 'Olá! Fiz o Checklist de Prontidão para Cirurgia e tenho interesse em contratar a consultoria pré-cirúrgica.'

  const proximosPassos = resultado.proximosPassos.length
    ? `Próximos passos que apareceram no meu relatório:\n- ${resultado.proximosPassos.join('\n- ')}`
    : 'Meu relatório indicou que já estou bem encaminhado para a cirurgia.'

  return encodeURIComponent(
    `${prefixo}\n\nNome: ${lead.nome}\nTelefone: ${lead.telefone}\nE-mail: ${lead.email}\nNível: ${resultado.nivel} (${resultado.scoreTotal}/${resultado.scoreMaximo})\n\n${proximosPassos}`
  )
}

const ChecklistProntidao = ({
  analyticsLabel = DEFAULT_PAGE_LABEL,
  onContratar,
  contratarLabel = 'Contratar agora',
}) => {
  const [questionIndex, setQuestionIndex] = useState(0)
  const [stage, setStage] = useState('questions')
  const [answers, setAnswers] = useState({})
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [leadData, setLeadData] = useState(LEAD_DATA_INICIAL)
  const [leadErrors, setLeadErrors] = useState({})
  const [leadEnviado, setLeadEnviado] = useState(null)
  const startTrackedRef = useRef(false)
  const pageLabel = analyticsLabel

  const currentQuestion = CHECKLIST_PERGUNTAS[questionIndex]
  const resultado = useMemo(() => calcularResultadoChecklist(answers), [answers])
  const totalSteps = CHECKLIST_PERGUNTAS.length + 1
  const currentStep =
    stage === 'questions' ? questionIndex + 1 : totalSteps
  const progresso = (currentStep / totalSteps) * 100

  const categoriaAtual = CHECKLIST_CATEGORIAS.find((c) => c.id === currentQuestion.categoria)

  useEffect(() => {
    capturarAttributionDaUrl()
  }, [])

  const trackChecklistStartIfNeeded = () => {
    if (startTrackedRef.current) return

    trackCustomEvent('checklist_start', 'form', pageLabel)
    startTrackedRef.current = true
  }

  const handleSelectAnswer = (value) => {
    trackChecklistStartIfNeeded()
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }))

    trackCustomEvent(
      `checklist_question_${questionIndex + 1}`,
      'form',
      `${pageLabel}_${currentQuestion.id}`
    )

    if (questionIndex === CHECKLIST_PERGUNTAS.length - 1) {
      setStage('gate')
      return
    }

    setQuestionIndex((prev) => prev + 1)
  }

  const handleBack = () => {
    setSubmitError('')

    if (stage === 'gate') {
      setStage('questions')
      setQuestionIndex(CHECKLIST_PERGUNTAS.length - 1)
      return
    }

    if (stage === 'result') {
      setStage('gate')
      return
    }

    setQuestionIndex((prev) => Math.max(prev - 1, 0))
  }

  const handleLeadChange = (field, value) => {
    setLeadData((prev) => ({
      ...prev,
      [field]: value,
    }))

    setLeadErrors((prev) => ({
      ...prev,
      [field]: '',
    }))
    setSubmitError('')
  }

  const validarLead = () => {
    const erros = {}

    if (!leadData.nome.trim()) {
      erros.nome = 'Informe seu nome para continuar.'
    }

    if (obterDigitosTelefone(leadData.telefone).length < 10) {
      erros.telefone = 'Informe um WhatsApp válido com DDD.'
    }

    if (!leadData.email.trim()) {
      erros.email = 'Informe seu e-mail.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadData.email)) {
      erros.email = 'Informe um e-mail válido.'
    }

    if (!leadData.aceita_privacidade) {
      erros.aceita_privacidade = 'Você precisa aceitar a política de privacidade.'
    }

    setLeadErrors(erros)
    return Object.keys(erros).length === 0
  }

  const liberarResultado = async (enviouLead, { eventId, email, telefone } = {}) => {
    setLeadEnviado(enviouLead)

    trackCustomEvent(
      'checklist_complete',
      'form',
      `${pageLabel}_${resultado.nivel.toLowerCase().replace(/\s+/g, '_')}`
    )

    if (enviouLead) {
      const pii =
        email || telefone
          ? await hashPII(email || '', telefone || '')
          : { sha256_email: undefined, sha256_phone: undefined }

      pushEvent({
        event: 'checklist_lead_submit',
        event_category: 'form',
        event_label: pageLabel,
        event_id: eventId,
        sha256_email: pii.sha256_email,
        sha256_phone: pii.sha256_phone,
      })
    } else {
      trackCustomEvent('checklist_lead_failed', 'form', pageLabel)
    }

    setStage('result')
  }

  const montarPayloadLead = () => ({
    nome: leadData.nome.trim(),
    telefone: leadData.telefone.trim(),
    email: leadData.email.trim().toLowerCase(),
    aceita_privacidade: leadData.aceita_privacidade,
    quer_avaliacao: leadData.quer_avaliacao,
    quer_contratar: leadData.quer_contratar,
    landing_path: typeof window !== 'undefined' ? window.location.pathname : '/consultoria-pre-cirurgica',
    ...obterAttributionSalva(),
    nivel_prontidao: resultado.nivel,
    score_total: resultado.scoreTotal,
    score_maximo: resultado.scoreMaximo,
    respostas: resultado.respostas.map((resposta) => ({
      id: resposta.id,
      categoria: resposta.categoria,
      pergunta: resposta.pergunta,
      resposta: resposta.resposta,
      rotulo: resposta.rotulo,
    })),
    resumo_categorias: resultado.categorias.map((categoria) => ({
      categoria: categoria.categoria,
      score: categoria.score,
      score_maximo: categoria.scoreMaximo,
      nivel: categoria.nivel,
    })),
  })

  const handleSubmitLead = async (event) => {
    event.preventDefault()

    if (!validarLead()) return

    setIsSubmitting(true)
    setSubmitError('')

    const eventId = crypto.randomUUID()
    const payload = {
      ...montarPayloadLead(),
      event_id: eventId,
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
    }

    try {
      const response = await fetch(getApiUrl('/lead-consultoria'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        await liberarResultado(true, {
          eventId,
          email: payload.email,
          telefone: payload.telefone,
        })
        return
      }

      await liberarResultado(false)
    } catch {
      await liberarResultado(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReiniciarChecklist = () => {
    const temProgresso =
      stage !== 'questions' || questionIndex > 0 || Object.keys(answers).length > 0

    if (
      temProgresso &&
      !window.confirm('Deseja reiniciar o checklist? Suas respostas atuais serão perdidas.')
    ) {
      return
    }

    trackCustomEvent('checklist_reset', 'form', `${pageLabel}_${stage}`)

    setQuestionIndex(0)
    setStage('questions')
    setAnswers({})
    setSubmitError('')
    setIsSubmitting(false)
    setLeadData(LEAD_DATA_INICIAL)
    setLeadErrors({})
    setLeadEnviado(null)
    startTrackedRef.current = false

    document.getElementById('checklist-prontidao')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleWhatsAppClick = (tipo) => {
    const eventName =
      tipo === 'avaliacao' ? 'cta_avaliacao_gratuita' : 'cta_contratar_whatsapp'
    const mensagem = montarMensagemWhatsApp(tipo, leadData, resultado)

    trackCustomEvent(eventName, 'button', pageLabel)
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${mensagem}`, '_blank')
  }

  const handleContratarClick = () => {
    if (typeof onContratar === 'function') {
      trackCustomEvent('cta_contratar_checkout', 'button', pageLabel)
      onContratar({ lead: leadData, resultado })
      return
    }

    handleWhatsAppClick('contratar')
  }

  const tituloEtapa =
    stage === 'questions'
      ? categoriaAtual?.nome
      : stage === 'gate'
        ? 'Veja seu relatório completo'
        : 'Seu relatório está pronto'

  return (
    <section id="checklist-prontidao" className="scroll-mt-24 relative py-20 md:py-28 px-4 bg-mist/40">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 space-y-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-teal-deep">
            <Sparkles className="h-3.5 w-3.5" />
            Ferramenta gratuita
          </span>
          <h2 className="font-bold text-3xl md:text-5xl text-navy leading-tight">
            Checklist de Prontidão para Cirurgia
          </h2>
          <p className="text-lg text-ink-soft max-w-2xl mx-auto">
            Responda em cerca de 2 minutos e receba um relatório imediato sobre seu nível de
            preparação.
          </p>
        </div>

        <Card className="border-0 shadow-lift rounded-3xl overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-navy text-cream px-6 md:px-10 py-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-teal-soft/90 mb-1">
                  Etapa {currentStep} de {totalSteps}
                </div>
                <div className="font-bold text-lg">{tituloEtapa}</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <div className="flex items-center gap-2 text-xs text-teal-soft">
                  <ShieldCheck className="h-4 w-4" />
                  Dados protegidos
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full border-cream/25 bg-cream/5 text-cream hover:bg-cream/15 hover:text-cream"
                  onClick={handleReiniciarChecklist}
                >
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  Reiniciar
                </Button>
              </div>
            </div>

            <div className="h-1.5 bg-mist">
              <div
                className="h-full bg-teal transition-all duration-500"
                style={{ width: `${progresso}%` }}
              />
            </div>

            <div className="p-6 md:p-10 space-y-8">
              {stage === 'questions' && (
                <>
                  <div>
                    <h3 className="text-xl md:text-2xl font-medium text-navy mb-2 leading-snug">
                      {currentQuestion.pergunta}
                    </h3>
                    <p className="text-sm text-ink-soft">{currentQuestion.ajuda}</p>
                  </div>

                  <div className="grid gap-3" role="radiogroup" aria-label={currentQuestion.pergunta}>
                    {CHECKLIST_RESPOSTAS.map((opcao) => {
                      const selected = answers[currentQuestion.id] === opcao.valor
                      return (
                        <button
                          key={opcao.valor}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => handleSelectAnswer(opcao.valor)}
                          className={`flex items-center gap-4 rounded-2xl border-2 px-5 py-4 cursor-pointer transition-all text-left w-full ${
                            selected
                              ? 'border-teal bg-teal/5 shadow-soft'
                              : 'border-border bg-card hover:border-teal-soft hover:bg-mist/30'
                          }`}
                        >
                          <span
                            className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                              selected ? 'border-teal bg-teal' : 'border-border bg-card'
                            }`}
                            aria-hidden
                          />
                          <span className="font-medium text-navy">{opcao.rotulo}</span>
                        </button>
                      )
                    })}
                  </div>

                  <div className="pt-2">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={questionIndex === 0}
                      size="lg"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Voltar
                    </Button>
                  </div>
                </>
              )}

              {stage === 'gate' && (
                <form className="space-y-6" onSubmit={handleSubmitLead}>
                  <div>
                    <h3 className="text-xl md:text-2xl font-medium text-navy mb-2 leading-snug">
                      Falta só um passo para liberar seu relatório
                    </h3>
                    <p className="text-sm text-ink-soft">
                      Deixe seus dados para receber uma avaliação mais personalizada do caso, caso
                      você queira apoio do nosso concierge.
                    </p>
                  </div>

                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="lead-nome">Nome completo</Label>
                      <Input
                        id="lead-nome"
                        value={leadData.nome}
                        onChange={(event) => handleLeadChange('nome', event.target.value)}
                        placeholder="Como você gostaria de ser chamado(a)?"
                      />
                      {leadErrors.nome && (
                        <p className="text-sm text-destructive">{leadErrors.nome}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lead-telefone">WhatsApp</Label>
                      <Input
                        id="lead-telefone"
                        value={leadData.telefone}
                        onChange={(event) =>
                          handleLeadChange('telefone', formatarTelefone(event.target.value))
                        }
                        placeholder="(85) 99999-9999"
                      />
                      {leadErrors.telefone && (
                        <p className="text-sm text-destructive">{leadErrors.telefone}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lead-email">E-mail</Label>
                      <Input
                        id="lead-email"
                        type="email"
                        value={leadData.email}
                        onChange={(event) => handleLeadChange('email', event.target.value)}
                        placeholder="voce@exemplo.com"
                      />
                      {leadErrors.email && (
                        <p className="text-sm text-destructive">{leadErrors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 rounded-2xl border border-teal/20 bg-teal/5 p-5">
                    <label className="flex items-start gap-3">
                      <Checkbox
                        checked={leadData.quer_avaliacao}
                        onCheckedChange={(checked) =>
                          handleLeadChange('quer_avaliacao', Boolean(checked))
                        }
                        className="mt-1"
                      />
                      <span className="text-sm text-ink">
                        Quero que um concierge revise meu caso gratuitamente e entre em contato
                        comigo para orientar os próximos passos.
                      </span>
                    </label>

                    <label className="flex items-start gap-3">
                      <Checkbox
                        checked={leadData.aceita_privacidade}
                        onCheckedChange={(checked) =>
                          handleLeadChange('aceita_privacidade', Boolean(checked))
                        }
                        className="mt-1"
                      />
                      <span className="text-sm text-ink">
                        Concordo com o tratamento dos meus dados para receber meu relatório e
                        contato da equipe, conforme a{' '}
                        <Link
                          to="/politica-de-privacidade"
                          className="font-semibold text-teal-deep underline underline-offset-4"
                        >
                          Política de Privacidade
                        </Link>
                        .
                      </span>
                    </label>

                    {leadErrors.aceita_privacidade && (
                      <p className="text-sm text-destructive">{leadErrors.aceita_privacidade}</p>
                    )}
                  </div>

                  {submitError && (
                    <Alert variant="destructive">
                      <AlertTitle>Não foi possível continuar</AlertTitle>
                      <AlertDescription>{submitError}</AlertDescription>
                    </Alert>
                  )}

                  <p className="text-sm text-ink-soft">
                    Seu relatório é calculado na hora no seu navegador. Mesmo que haja instabilidade
                    temporária no envio dos dados, você ainda verá o resultado completo.
                  </p>

                  <div className="flex justify-between gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={handleBack} size="lg">
                      <ArrowLeft className="h-4 w-4" />
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      size="lg"
                      className="bg-teal hover:bg-teal-deep text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          Ver meu relatório agora
                          <Sparkles className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}

              {stage === 'result' && (
                <div className="space-y-8">
                  <div className="rounded-2xl bg-gradient-to-br from-teal-deep to-navy text-cream p-6 md:p-8">
                    <div className="flex items-center gap-2 text-teal-soft mb-3 text-sm">
                      <CheckCircle2 className="h-4 w-4" />
                      Resultado liberado
                    </div>
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                      <div>
                        <h3 className="font-bold text-3xl md:text-4xl mb-2">
                          {resultado.nivel}
                        </h3>
                        <p className="text-cream/80 max-w-lg text-sm leading-relaxed">
                          {resultado.descricaoNivel}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-wider text-teal-soft mb-1">
                          Prontidão
                        </div>
                        <div className="font-bold text-4xl">
                          {resultado.scoreTotal}
                          <span className="text-xl text-cream/60">/{resultado.scoreMaximo}</span>
                        </div>
                      </div>
                    </div>
                    <Progress
                      value={resultado.percentualGeral * 100}
                      className="mt-6 bg-cream/10 [&>div]:bg-teal-soft"
                    />
                  </div>

                  <div className="grid gap-3">
                    {resultado.categorias.map((cat) => (
                      <div
                        key={cat.id}
                        className={`rounded-xl border-l-4 ${obterTomCategoria(cat.nivel)} px-5 py-4`}
                      >
                        <div className="flex justify-between items-start gap-3 mb-1">
                          <h4 className="font-medium text-navy">{cat.categoria}</h4>
                          <span className="text-xs font-medium text-ink-soft whitespace-nowrap">
                            {cat.score}/{cat.scoreMaximo} · {cat.nivel}
                          </span>
                        </div>
                        {cat.pendencias.length > 0 && (
                          <p className="text-sm text-ink-soft">{cat.pendencias[0]}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {resultado.proximosPassos.length > 0 && (
                    <div className="rounded-2xl bg-mist/50 p-6">
                      <div className="flex items-center gap-2 mb-4 text-navy">
                        <ClipboardList className="h-5 w-5 text-teal" />
                        <h4 className="font-medium">O que vale priorizar agora</h4>
                      </div>
                      <ul className="space-y-2.5">
                        {resultado.proximosPassos.map((p) => (
                          <li key={p} className="flex gap-3 text-sm text-ink">
                            <CheckCircle2 className="h-4 w-4 text-teal mt-0.5 shrink-0" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Alert className="border-teal/30 bg-teal/5">
                    <MessageSquareHeart className="h-4 w-4 text-teal-deep" />
                    <AlertTitle className="text-navy">
                      {leadEnviado === false
                        ? 'Seu relatório está disponível, mas não conseguimos registrar seus dados agora'
                        : leadData.quer_avaliacao
                          ? 'Nossa equipe já pode revisar seu caso'
                          : 'Seu relatório já está disponível'}
                    </AlertTitle>
                    <AlertDescription className="text-ink-soft">
                      {leadEnviado === false
                        ? 'Houve instabilidade ao enviar suas informações. Use o WhatsApp abaixo para solicitar sua avaliação gratuita com o resumo do seu checklist.'
                        : leadData.quer_avaliacao
                          ? 'Como você pediu uma avaliação gratuita, o concierge pode usar esse diagnóstico para orientar seus próximos passos.'
                          : 'Se quiser apoio humano para revisar autorizações, exames e preparo, você ainda pode falar com o concierge agora.'}
                    </AlertDescription>
                  </Alert>

                  <div className="flex flex-col sm:flex-row gap-3">
                    {typeof onContratar === 'function' ? (
                      <Button
                        size="lg"
                        onClick={handleContratarClick}
                        className="bg-teal hover:bg-teal-deep text-white flex-1"
                      >
                        {contratarLabel}
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        onClick={() => handleWhatsAppClick('avaliacao')}
                        className="bg-teal hover:bg-teal-deep text-white flex-1"
                      >
                        <MessageCircle className="h-5 w-5" />
                        Solicitar avaliação gratuita
                      </Button>
                    )}
                    {typeof onContratar !== 'function' && (
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={handleContratarClick}
                      >
                        Contratar pelo WhatsApp
                      </Button>
                    )}
                    <Button size="lg" variant="outline" onClick={handleReiniciarChecklist}>
                      <RotateCcw className="h-4 w-4" />
                      Refazer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}

export default ChecklistProntidao
